// B2b-3 global search. GET /api/search (server filters by tenant → module flag → project access → role; it never
// reveals filtered-out hits). Desktop: header input + listbox. Phones (<640px): a 44px button opens a full-screen
// overlay. Cmd/Ctrl+K opens, ↑/↓ moves, Enter opens, Esc closes. Debounced 250 ms, min 2 characters.
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import { api } from "../lib/api";

export const SEARCH_ENABLED = true;

const TYPE_LABELS = {
  project: "Project", lead: "Lead", contact: "Contact", account: "Account", contract: "Contract", rfi: "RFI",
  submittal: "Submittal", change_order: "Change order", document: "Drawing", daily_log: "Daily log",
  property: "Property", vendor: "Vendor", vault_document: "Document",
};

export function useSearch(q) {
  const [state, setState] = useState({ status: "idle", results: [], message: "" });
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setState({ status: "idle", results: [], message: "" }); return undefined; }
    let live = true;
    setState((s) => ({ ...s, status: "loading" }));
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/search", { params: { q: term } });
        if (live) setState({ status: "done", results: r.data.results || [], message: "" });
      } catch (e) {
        const d = e?.response?.data?.detail || {};
        const message = e?.response?.status === 429 ? "Too many searches. Try again in a minute."
          : d.message || "Search is unavailable right now.";
        if (live) setState({ status: "error", results: [], message });
      }
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [q]);
  return state;
}

export function SearchResults({ state, active, onPick, idPrefix }) {
  if (state.status === "idle") return <p className="px-3 py-3 text-xs text-erf-dark/60" data-testid="global-search-hint">Type at least 2 characters.</p>;
  if (state.status === "loading") return <p className="px-3 py-3 text-xs text-erf-dark/60" role="status" data-testid="global-search-loading">Searching…</p>;
  if (state.status === "error") return <p className="px-3 py-3 text-xs text-red-700" role="alert" data-testid="global-search-error">{state.message}</p>;
  if (!state.results.length) return <p className="px-3 py-3 text-xs text-erf-dark/70" role="status" data-testid="global-search-empty">No results you have access to.</p>;
  return (
    <ul role="listbox" id={`${idPrefix}-listbox`} aria-label="Search results" className="py-1" data-testid="global-search-results">
      {state.results.map((r, i) => (
        <li key={`${r.type}:${r.id}`} id={`${idPrefix}-opt-${i}`} role="option" aria-selected={i === active}>
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => onPick(r)}
            className={`w-full min-h-[44px] text-left px-3 py-2 flex flex-col focus:outline-none focus:ring-2 focus:ring-erf-gold ${i === active ? "bg-erf-creamSecondary" : "hover:bg-erf-cream"}`}
            data-testid={`global-search-result-${i}`}>
            <span className="text-sm font-nav text-erf-dark break-words">{r.title}</span>
            <span className="text-[11px] text-erf-dark/60">
              {TYPE_LABELS[r.type] || r.type}{r.subtitle ? ` · ${r.subtitle}` : ""}{r.project?.name ? ` · ${r.project.name}` : ""}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function useSearchBox(onDone) {
  const [q, setQ] = useState("");
  const [active, setActive] = useState(-1);
  const state = useSearch(q);
  const navigate = useNavigate();
  useEffect(() => setActive(-1), [state.results]);
  const pick = (r) => { navigate(r.url); setQ(""); onDone(); };
  const onKeyDown = (e) => {
    const n = state.results.length;
    if (e.key === "ArrowDown" && n) { e.preventDefault(); setActive((a) => (a + 1) % n); }
    else if (e.key === "ArrowUp" && n) { e.preventDefault(); setActive((a) => (a <= 0 ? n - 1 : a - 1)); }
    else if (e.key === "Enter" && n) { e.preventDefault(); pick(state.results[active >= 0 ? active : 0]); }
    else if (e.key === "Escape") { setQ(""); onDone(); }
  };
  return { q, setQ, active, state, pick, onKeyDown };
}

function SearchInput({ box, idPrefix, inputRef, open, onFocus }) {
  return (
    <div className="relative flex items-center">
      <Search className="absolute left-3 w-4 h-4 text-erf-dark/50" aria-hidden="true" />
      <input ref={inputRef} type="search" value={box.q} onChange={(e) => box.setQ(e.target.value)} onKeyDown={box.onKeyDown}
        onFocus={onFocus} placeholder="Search projects, RFIs, leads… (Ctrl+K)" aria-label="Search"
        role="combobox" aria-expanded={open} aria-controls={`${idPrefix}-listbox`}
        aria-activedescendant={box.active >= 0 ? `${idPrefix}-opt-${box.active}` : undefined}
        className="w-full !min-h-[44px] rounded border border-erf-borderCream bg-white !pl-9 !pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-erf-gold"
        data-testid={`${idPrefix}-input`} />
    </div>
  );
}

export default function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [sheet, setSheet] = useState(false);
  const desktopRef = useRef(null);
  const sheetRef = useRef(null);
  const close = () => { setOpen(false); setSheet(false); };
  const desk = useSearchBox(close);
  const mob = useSearchBox(close);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        if (window.matchMedia("(max-width: 639px)").matches) setSheet(true);
        else { setOpen(true); desktopRef.current?.focus(); }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  useEffect(() => { if (sheet) sheetRef.current?.focus(); }, [sheet]);

  if (!SEARCH_ENABLED) return null;
  return (
    <div className="relative" data-testid="global-search">
      <div className="hidden sm:block w-[min(420px,40vw)]" onBlur={() => setTimeout(() => setOpen(false), 120)}>
        <SearchInput box={desk} idPrefix="global-search" inputRef={desktopRef} open={open} onFocus={() => setOpen(true)} />
        {open && (
          <div className="absolute z-50 mt-1 w-full max-h-[60vh] overflow-auto rounded-md border border-erf-borderCream bg-white shadow-lg"
            data-testid="global-search-panel">
            <SearchResults state={desk.state} active={desk.active} onPick={desk.pick} idPrefix="global-search" />
          </div>
        )}
      </div>
      <button type="button" className="sm:hidden inline-flex items-center justify-center w-11 h-11 rounded focus:outline-none focus:ring-2 focus:ring-erf-gold"
        aria-label="Open search" onClick={() => setSheet(true)} data-testid="global-search-open-mobile">
        <Search className="w-5 h-5" aria-hidden="true" />
      </button>
      {sheet && (
        <div className="fixed inset-0 z-[60] bg-white flex flex-col" role="dialog" aria-modal="true" aria-label="Search"
          data-testid="global-search-sheet">
          <div className="flex items-center gap-2 p-3 border-b border-erf-borderCream">
            <div className="flex-1"><SearchInput box={mob} idPrefix="global-search-mobile" inputRef={sheetRef} open /></div>
            <button type="button" onClick={close} aria-label="Close search" data-testid="global-search-close"
              className="inline-flex items-center justify-center w-11 h-11 rounded focus:outline-none focus:ring-2 focus:ring-erf-gold">
              <X className="w-5 h-5" aria-hidden="true" />
            </button>
          </div>
          <div className="flex-1 overflow-auto">
            <SearchResults state={mob.state} active={mob.active} onPick={mob.pick} idPrefix="global-search-mobile" />
          </div>
        </div>
      )}
    </div>
  );
}
